import { fetchAuthSession } from "aws-amplify/auth";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com";

export type AlertTrigger =
  | "motion"
  | "person"
  | "car"
  | "bicycle"
  | "animal"
  | "boat"
  | "loitering";

export type AlertRule = {
  alertRuleId: string;
  tenantId: string;
  name: string;
  cameraIds: string[];
  triggers: AlertTrigger[];
  emailRecipients: string[];
  notificationFilterMinutes: number;
  enabled: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type SaveAlertRuleInput = {
  alertRuleId?: string;
  name: string;
  cameraIds: string[];
  triggers: AlertTrigger[];
  emailRecipients: string[];
  notificationFilterMinutes: number;
  enabled: boolean;
};

async function authHeaders() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error("Your session has expired. Sign in again.");
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

async function request(url: string, init: RequestInit) {
  try {
    return await fetch(url, init);
  } catch {
    throw new Error(
      "Alerts service is unreachable. The Alerts API may not be deployed yet or API Gateway is not responding.",
    );
  }
}

async function parseResponse(response: Response) {
  const text = await response.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }
  if (!response.ok) {
    throw new Error(payload?.message || text || `Request failed (${response.status}).`);
  }
  return payload;
}

export async function listAlertRules(): Promise<AlertRule[]> {
  const response = await request(`${API_BASE}/alerts`, {
    method: "GET",
    headers: await authHeaders(),
  });
  const payload = await parseResponse(response);
  return Array.isArray(payload?.items) ? payload.items : [];
}

export async function saveAlertRule(input: SaveAlertRuleInput): Promise<AlertRule> {
  const editing = Boolean(input.alertRuleId);
  const response = await request(
    editing ? `${API_BASE}/alerts/${encodeURIComponent(input.alertRuleId!)}` : `${API_BASE}/alerts`,
    {
      method: editing ? "PUT" : "POST",
      headers: await authHeaders(),
      body: JSON.stringify(input),
    },
  );
  const payload = await parseResponse(response);
  return payload?.item as AlertRule;
}

export async function deleteAlertRule(alertRuleId: string): Promise<void> {
  const response = await request(`${API_BASE}/alerts/${encodeURIComponent(alertRuleId)}`, {
    method: "DELETE",
    headers: await authHeaders(),
  });
  await parseResponse(response);
}
